/**
 * v1.1.44+ 折叠态：把毫秒格式化为『Xm Ys』/『Xh Ym Zs』/『Ys』三种形式。
 * 输入为负数或 null/undefined 时返回 null。
 */
export function formatTaskDuration(durationMs: number | null | undefined): string | null {
  if (durationMs == null || !Number.isFinite(durationMs) || durationMs < 0) {
    return null
  }
  const totalSec = Math.floor(durationMs / 1000)
  if (totalSec < 60) {
    return `${totalSec}s`
  }
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  if (h > 0) {
    return `${h}h ${m}m ${s}s`
  }
  return `${m}m ${s}s`
}

function stripMarkdownCodeFence(text: string): string {
  const fenced = text.match(/^```(?:json)?\s*([\s\S]*?)```\s*$/i)
  if (fenced) {
    return fenced[1].trim()
  }
  return text
}

/** Removes an internal structured block accidentally persisted after visible text. */
function stripTrailingInternalCodeFence(text: string): string {
  return text.replace(/\n*```(?:json)?\s*\{[\s\S]*?```\s*$/i, '').trim()
}

/** Fields that identify a historical internal planning payload. */
function looksLikeInternalPlanJson(text: string): boolean {
  return (
    text.includes('"objectives"')
    || text.includes('"knowledgeQuery"')
    || text.includes('"materialQuery"')
    || text.includes('"questions"')
    || text.includes('"tasks"')
  )
}

export function isInternalPlanJsonText(text: string | null | undefined): boolean {
  const normalized = stripMarkdownCodeFence((text ?? '').trim())
  return normalized.startsWith('{') && looksLikeInternalPlanJson(normalized)
}

function pickVisibleReplyFromParsed(parsed: {
  replyToUser?: unknown
  searchHint?: unknown
  askHint?: unknown
}): string | null {
  if (typeof parsed.replyToUser === 'string' && parsed.replyToUser.trim()) {
    return parsed.replyToUser.trim()
  }
  if (typeof parsed.searchHint === 'string' && parsed.searchHint.trim()) {
    return parsed.searchHint.trim()
  }
  if (typeof parsed.askHint === 'string' && parsed.askHint.trim()) {
    return parsed.askHint.trim()
  }
  return null
}

function extractReplyFromCompletePlanJson(text: string): string | null {
  try {
    const parsed = JSON.parse(text) as {
      tasks?: unknown
      replyToUser?: unknown
      searchHint?: unknown
      askHint?: unknown
    }
    const tasks = parsed.tasks
    const hasTasks = Array.isArray(tasks) && tasks.length > 0
    if (hasTasks) {
      return pickVisibleReplyFromParsed(parsed) ?? ''
    }
    return pickVisibleReplyFromParsed(parsed)
  } catch {
    return null
  }
}

function extractReplyToUserFromPartialJson(text: string): string | null {
  for (const key of ['replyToUser', 'searchHint', 'askHint'] as const) {
    const idx = text.indexOf(`"${key}"`)
    if (idx < 0) {
      continue
    }
    const rest = text.slice(idx)
    const match = rest.match(new RegExp(`"${key}"\\s*:\\s*"((?:\\\\.|[^"\\\\])*)(?:"|$)`))
    if (!match?.[1]) {
      continue
    }
    try {
      return JSON.parse(`"${match[1]}"`) as string
    } catch {
      return match[1]
        .replace(/\\n/g, '\n')
        .replace(/\\"/g, '"')
        .replace(/\\\\/g, '\\')
    }
  }
  return null
}

/** JSON 对象闭合后紧跟的纯文本（如 content_delta 把 finalText 追加在 plan JSON 之后）。 */
function plainTextAfterPlanJson(text: string): string {
  const start = text.indexOf('{')
  if (start < 0) {
    return ''
  }
  let depth = 0
  let inString = false
  let escape = false
  for (let i = start; i < text.length; i++) {
    const c = text[i]
    if (inString) {
      if (escape) {
        escape = false
      } else if (c === '\\') {
        escape = true
      } else if (c === '"') {
        inString = false
      }
      continue
    }
    if (c === '"') {
      inString = true
      continue
    }
    if (c === '{') {
      depth++
    } else if (c === '}') {
      depth--
      if (depth === 0) {
        return text.slice(i + 1).trim()
      }
    }
  }
  return ''
}

function resolvePurePlanJson(unfenced: string): string {
  const fromComplete = extractReplyFromCompletePlanJson(unfenced)
  if (fromComplete !== null) {
    return fromComplete
  }
  const suffix = plainTextAfterPlanJson(unfenced)
  if (suffix) {
    return suffix
  }
  if (looksLikeInternalPlanJson(unfenced)) {
    return extractReplyToUserFromPartialJson(unfenced) ?? ''
  }
  try {
    const parsed = JSON.parse(unfenced) as { replyToUser?: unknown }
    if (typeof parsed.replyToUser === 'string' && parsed.replyToUser.trim()) {
      return parsed.replyToUser.trim()
    }
  } catch {
    /* partial or non-plan JSON */
  }
  return ''
}

function resolveEmbeddedPlanJson(text: string): string | null {
  const withoutFence = stripTrailingInternalCodeFence(text)
  if (withoutFence !== text.trim()) {
    return withoutFence
  }

  const jsonStart = text.indexOf('{')
  if (jsonStart < 0 || !looksLikeInternalPlanJson(text.slice(jsonStart))) {
    return null
  }
  const before = text.slice(0, jsonStart).trim()
  const jsonPart = text.slice(jsonStart)
  const suffix = plainTextAfterPlanJson(jsonPart)
  const fromJson = resolvePurePlanJson(jsonPart.trim())
  if (before) {
    return before
  }
  if (suffix) {
    return suffix
  }
  if (fromJson) {
    return fromJson
  }
  return ''
}

/**
 * Historical internal planning JSON is never rendered directly; only its user-facing reply is.
 * 支持：纯 JSON、流式未收齐 JSON、正文与 JSON 混排、markdown 代码块包裹的 JSON。
 */
export function resolveAssistantVisibleText(raw: string | null | undefined): string {
  let text = (raw ?? '').trim()
  if (!text) {
    return ''
  }

  text = stripTrailingInternalCodeFence(text)
  const unfenced = stripMarkdownCodeFence(text)

  const embedded = resolveEmbeddedPlanJson(unfenced)
  if (embedded !== null) {
    return embedded
  }

  if (unfenced.startsWith('{')) {
    return resolvePurePlanJson(unfenced)
  }

  return text
}
